import { type JSX, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Chip, Spinner } from '@heroui/react';

import { usePatternDetail, usePatternManifest } from '~/hooks/useDesignPatterns';

import { PartOfArchitectures } from './components/PartOfArchitectures';
import { PatternBreadcrumb } from './components/PatternBreadcrumb';
import { PatternContent } from './components/PatternContent';
import { PatternExamples } from './components/PatternExamples';
import { PatternRelationships } from './components/PatternRelationships';
import { SeverityChip } from './components/SeverityChip';
import { formatDomainLabel, formatPatternTitle } from './utils';

type PatternTab = 'pattern' | 'examples';

export function PatternPage(): JSX.Element {
  const { patternId } = useParams<{ patternId: string }>();
  const { error, isLoading, pattern } = usePatternDetail(patternId);
  const { manifest } = usePatternManifest();
  const [activeTab, setActiveTab] = useState<PatternTab>('pattern');

  // Reset tab when navigating to a different pattern
  useEffect(() => {
    setActiveTab('pattern');
  }, [patternId]);

  // Get full manifest entry for metadata not in the detail response
  const manifestEntry = useMemo(
    () => manifest?.patterns.find(p => p.pattern_id === patternId),
    [manifest, patternId],
  );

  const hasExamples = (pattern?.example_stacks.length ?? 0) > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !pattern) {
    return (
      <div className="py-20 text-center text-error">
        {error ? 'Failed to load pattern.' : 'Pattern not found.'}
      </div>
    );
  }

  const tabs: Array<{ key: PatternTab; label: string }> = [
    { key: 'pattern', label: 'Pattern' },
    ...(hasExamples ? [{ key: 'examples' as const, label: 'Examples' }] : []),
  ];

  return (
    <div>
      <PatternBreadcrumb domain={pattern.domain} patternId={pattern.pattern_id} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-3">
          {formatPatternTitle(pattern.pattern_id)}
        </h1>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <SeverityChip severity={pattern.severity} size="md" />
          {manifestEntry?.type === 'anti-pattern' && (
            <Chip color="warning" variant="flat">
              Anti-Pattern
            </Chip>
          )}
          <Chip variant="bordered">{formatDomainLabel(pattern.domain)}</Chip>
        </div>

        {/* Tech stack and applies_to chips */}
        {manifestEntry && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(manifestEntry.tech_stack ?? []).map(tech => (
              <Chip key={tech} size="sm" variant="flat" color="secondary">
                {tech}
              </Chip>
            ))}
            {manifestEntry.applies_to.map(scope => (
              <Chip key={scope} size="sm" variant="bordered">
                {scope}
              </Chip>
            ))}
          </div>
        )}

        <p className="text-muted">{pattern.intent}</p>
      </div>

      {/* Top-level tab bar */}
      <div className="flex gap-0 border-b border-default mb-6">
        {tabs.map(tab => {
          const active = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                px-5 py-2.5 font-semibold rounded-t-md transition-all cursor-pointer
                border-b-2 -mb-px
                ${
                  active
                    ? 'border-primary text-primary bg-primary/10'
                    : 'border-transparent text-muted hover:text-foreground hover:bg-default/40'
                }
              `}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'pattern' && (
        <>
          <PatternContent content={pattern.content} domain={pattern.domain} />
          {manifestEntry && (
            <PatternRelationships
              requires={manifestEntry.requires ?? []}
              enhances={manifestEntry.enhances ?? []}
            />
          )}
          <PartOfArchitectures patternId={pattern.pattern_id} />
        </>
      )}

      {activeTab === 'examples' && hasExamples && (
        <PatternExamples
          patternId={pattern.pattern_id}
          exampleStacks={pattern.example_stacks}
          domain={pattern.domain}
        />
      )}
    </div>
  );
}
